import { Button } from '@creditornot/cb-button';
import { InputContainer } from '@creditornot/cb-form';
import { typographyCss, cssThemedValue } from '@creditornot/cb-ingredients';
import { Textarea } from '@creditornot/cb-input';
import { ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { MultiSelect } from '@creditornot/cb-select';
import React, { useCallback, useMemo } from 'react';
import { Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { DietaryPreference, MandatoryWarning, OptionType } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { WoltModal } from 'common/components';
import { mandatoryWarningsForCountry, mandatoryWarningTranslationMap } from 'modules/menu/mandatoryWarnings';
import { FormOptionValue } from 'modules/menu/types';
import { useOfferingMenuFeaturesContext } from 'modules/shared-menu/contexts/OfferingMenuFeaturesContext';
import { useSelectionInfoContext } from 'modules/shared-menu/contexts/SelectionInfoContext';

import {
  dietaryPreferenceTranslationMap,
  INGREDIENTS_FIELD_MAX_LENGTH,
  PRODUCT_INFO_FIELD_MAX_LENGTH,
} from '../../constants';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const MAX_TEXTAREA_ROWS = 10;

const Root = styled(WoltModal)`
  max-height: 90vh;
  max-width: 1000px;
  width: 100%;
  border-radius: 8px;
  background: ${cssThemedValue('surfaceMain')};
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
`;

const Container = styled.form`
  flex: 1 1 auto;
  overflow-x: hidden;
  overflow-y: auto;
  position: relative;
  background-color: ${cssThemedValue('surfaceMain')};
`;

const Content = styled.div`
  flex: 1 1 auto;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 24px;
`;

const StyledInputContainer = styled(InputContainer)`
  width: 100%;
`;

const StyledTextarea = styled(Textarea).attrs({
  autoresizeMaxRows: MAX_TEXTAREA_ROWS,
  rows: 1,
})``;

const Section = styled.div`
  & + & {
    margin-block-start: 24px;
  }
`;

const SectionTitle = styled.span`
  ${typographyCss.Title1};
  display: block;
  margin-block-end: 16px;
`;

const Fields = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-inline-start: -8px;
  margin-inline-end: -8px;
  margin-block-end: -8px;
`;

const FormField = styled.div`
  flex: 0 0 50%;
  padding-inline-start: 8px;
  padding-inline-end: 8px;
  margin-block-end: 8px;
`;

const Label = styled.label`
  ${typographyCss.Small};
  display: block;
  margin-block-end: 4px;
`;

const Footer = styled(ModalFooter)`
  justify-content: flex-end;
`;

export const OptionValueAdvancedDetailsModal = ({ isOpen, onClose }: Props) => {
  const intl = useIntl();
  const { country } = useSelectionInfoContext();
  const menuFeatures = useOfferingMenuFeaturesContext();

  const handleSubmit = useCallback(
    event => {
      event.preventDefault();
      onClose();
    },
    [onClose],
  );

  const dietaryPreferenceOptions = useMemo(() => {
    return Object.entries(dietaryPreferenceTranslationMap).map(([key, value]) => ({
      value: key,
      label: intl.formatMessage({ id: value }),
    }));
  }, [intl]);

  const mandatoryWarningOptions: { value: MandatoryWarning; label: string }[] = useMemo(() => {
    return (
      mandatoryWarningsForCountry[country]?.map((warningForCountry: MandatoryWarning) => ({
        value: warningForCountry,
        label: intl.formatMessage({ id: mandatoryWarningTranslationMap[warningForCountry] }),
      })) || []
    );
  }, [country, intl]);

  return (
    <Root isOpen={isOpen} onRequestClose={onClose}>
      <ModalHeader onCloseClick={onClose}>
        <FormattedMessage id="menu-option.advanced-details" />
      </ModalHeader>
      <Container onSubmit={handleSubmit}>
        <Content>
          <Field<OptionType> name="type">
            {({ input: { value: optionType } }) => (
              <FieldArray<FormOptionValue> name={optionType === 'bool' ? 'boolValues' : 'values'}>
                {({ fields }) =>
                  fields.map((name, index) => (
                    <Section key={name}>
                      <Field<Localized> name={`${name}.name`}>
                        {({ input }) => <SectionTitle>{input.value || '-'}</SectionTitle>}
                      </Field>
                      <Fields>
                        <FormField>
                          <Label>
                            <FormattedMessage id="menu.ingredients" />
                          </Label>
                          <Field<string> name={`${name}.ingredients`} parse={v => v}>
                            {({ input }) => (
                              <StyledInputContainer
                                characterCount={{ value: input.value.length, max: INGREDIENTS_FIELD_MAX_LENGTH }}
                              >
                                <StyledTextarea
                                  autoFocus={index === 0}
                                  {...input}
                                  maxLength={INGREDIENTS_FIELD_MAX_LENGTH}
                                  placeholder={intl.formatMessage({ id: 'menu.ingredients-placeholder' })}
                                />
                              </StyledInputContainer>
                            )}
                          </Field>
                        </FormField>
                        <FormField>
                          <Label>
                            <FormattedMessage id="menu.additives" />
                          </Label>
                          <Field<string> name={`${name}.additives`} parse={v => v}>
                            {({ input }) => (
                              <StyledInputContainer
                                characterCount={{ value: input.value.length, max: PRODUCT_INFO_FIELD_MAX_LENGTH }}
                              >
                                <StyledTextarea
                                  {...input}
                                  maxLength={PRODUCT_INFO_FIELD_MAX_LENGTH}
                                  placeholder={intl.formatMessage({ id: 'menu.additives-placeholder' })}
                                />
                              </StyledInputContainer>
                            )}
                          </Field>
                        </FormField>
                        <FormField>
                          <Label>
                            <FormattedMessage id="menu.nutrition-facts" />
                          </Label>
                          <Field<string> name={`${name}.nutritionFacts`} parse={v => v}>
                            {({ input }) => (
                              <StyledInputContainer
                                characterCount={{ value: input.value.length, max: PRODUCT_INFO_FIELD_MAX_LENGTH }}
                              >
                                <StyledTextarea
                                  {...input}
                                  maxLength={PRODUCT_INFO_FIELD_MAX_LENGTH}
                                  placeholder={intl.formatMessage({ id: 'menu.placeholder-unset' })}
                                />
                              </StyledInputContainer>
                            )}
                          </Field>
                        </FormField>
                        <FormField>
                          <Label>
                            <FormattedMessage id="menu.allergens" />
                          </Label>
                          <Field<string> name={`${name}.allergens`} parse={v => v}>
                            {({ input }) => (
                              <StyledInputContainer
                                characterCount={{ value: input.value.length, max: PRODUCT_INFO_FIELD_MAX_LENGTH }}
                              >
                                <StyledTextarea
                                  {...input}
                                  maxLength={PRODUCT_INFO_FIELD_MAX_LENGTH}
                                  placeholder={intl.formatMessage({ id: 'menu.allergens-placeholder' })}
                                />
                              </StyledInputContainer>
                            )}
                          </Field>
                        </FormField>
                        <FormField>
                          <Label>
                            <FormattedMessage id="menu.producer-information" />
                          </Label>
                          <Field<string> name={`${name}.producerInformation`} parse={v => v}>
                            {({ input }) => (
                              <StyledInputContainer
                                characterCount={{ value: input.value.length, max: PRODUCT_INFO_FIELD_MAX_LENGTH }}
                              >
                                <StyledTextarea
                                  {...input}
                                  maxLength={PRODUCT_INFO_FIELD_MAX_LENGTH}
                                  placeholder={intl.formatMessage({ id: 'menu.placeholder-unset' })}
                                />
                              </StyledInputContainer>
                            )}
                          </Field>
                        </FormField>
                        <FormField>
                          <Label>
                            <FormattedMessage id="menu.distributor-information" />
                          </Label>
                          <Field<string> name={`${name}.distributorInformation`} parse={v => v}>
                            {({ input }) => (
                              <StyledInputContainer
                                characterCount={{ value: input.value.length, max: PRODUCT_INFO_FIELD_MAX_LENGTH }}
                              >
                                <StyledTextarea
                                  {...input}
                                  maxLength={PRODUCT_INFO_FIELD_MAX_LENGTH}
                                  placeholder={intl.formatMessage({ id: 'menu.placeholder-unset' })}
                                />
                              </StyledInputContainer>
                            )}
                          </Field>
                        </FormField>
                        <FormField>
                          <Label>
                            <FormattedMessage id="menu.dietaryPreferences.title" />
                          </Label>
                          <Field<DietaryPreference[]> name={`${name}.dietaryPreferences`}>
                            {field => <MultiSelect {...field.input} options={dietaryPreferenceOptions} />}
                          </Field>
                        </FormField>
                        {mandatoryWarningOptions.length > 0 && menuFeatures.mandatoryWarnings && (
                          <FormField>
                            <Label>
                              <FormattedMessage id="menu.mandatoryWarnings.title" />
                            </Label>
                            <Field<DietaryPreference[]> name={`${name}.mandatoryWarnings`}>
                              {field => <MultiSelect {...field.input} options={mandatoryWarningOptions} />}
                            </Field>
                          </FormField>
                        )}
                      </Fields>
                    </Section>
                  ))
                }
              </FieldArray>
            )}
          </Field>
        </Content>
      </Container>
      <Footer>
        <Button size="small" variant="blue" onClick={onClose}>
          <FormattedMessage id="state.done" />
        </Button>
      </Footer>
    </Root>
  );
};
